import { WinDeriv_CalculatedBase, WinDerivBase, WinDerivSelection } from "../../interfaces/dtos/common";
import { bettingPhases, providers } from "../../variables";

export const getWinDerivCommonCalculated = (winDeriv: WinDerivBase): WinDeriv_CalculatedBase => {
    return {
        isSuspended: isSuspended(winDeriv),
        isAutoSuspended: isAutoSuspended(winDeriv),
        isManuallySuspended: isManuallySuspended(winDeriv),
        isSupplierSuspended: isSupplierSuspended(winDeriv),
        suspendedReason: suspendedReason(winDeriv),
        isEarlyPrice: isEarlyPrice(winDeriv),
        isBoardPrice: isBoardPrice(winDeriv),
        isStartingPrice: isStartingPrice(winDeriv),
        isIgnorePrice: isIgnorePrice(winDeriv),
        isBetmakerPrice: isBetmakerPrice(winDeriv),
        isDisplayOnly: isDisplayOnly(winDeriv),
        isDistributed: isDistributed(winDeriv),
        isRejected: isRejected(winDeriv),
    }
}

const isRejected = (winDeriv: WinDerivBase) => winDeriv?.rejectionDetails?.rejected.toLowerCase() !== 'none'

const isSuspended = (winDeriv: WinDerivBase) => {
    const none = "none";
    // If rejection is none, then look at suspension
    return winDeriv.suspensionDetails ? winDeriv.suspensionDetails.suspended.toLowerCase() !== none : false;
}

const isAutoSuspended = (winDeriv: WinDerivBase) => {
    return isSuspendedType(winDeriv, "auto");
}

const isManuallySuspended = (winDeriv: WinDerivBase) => {
    return isSuspendedType(winDeriv, "manual");
}

const isSupplierSuspended = (winDeriv: WinDerivBase) => {
    return hasSuspendedReason(winDeriv, "supplier_suspended");
}

const isSuspendedType = (winDeriv: WinDerivBase, type: string) => {
    const none = "none";
    if (!isSuspended) { return false; }
    // Rejection takes priority
    if (winDeriv.rejectionDetails && winDeriv.rejectionDetails.rejected.toLowerCase() !== none) {
        return winDeriv.rejectionDetails.rejected.toLowerCase() === type;
    }
    // If rejection is none, then look at suspension
    return winDeriv.suspensionDetails ? winDeriv.suspensionDetails.suspended?.toLowerCase() === type : false;
}

const hasSuspendedReason = (winDeriv: WinDerivBase, reason: string) => {
    const none = "none";
    if (!isSuspended) { return false; }
    // Rejection takes priority
    if (winDeriv.rejectionDetails && winDeriv.rejectionDetails.rejected.toLowerCase() !== none) {
        return winDeriv.rejectionDetails.rejectedReason?.toLowerCase() === reason;
    }
    // If rejection is none, then look at suspension
    if (winDeriv.suspensionDetails && winDeriv.suspensionDetails.suspended.toLowerCase() !== none) {
        return winDeriv.suspensionDetails.suspendedReason?.toLowerCase() === reason;
    }
    return false;
}

const suspendedReason = (winDeriv: WinDerivBase) => {
    const none = "none";
    if (!isSuspended) { return undefined; }
    if (winDeriv.suspensionDetails && winDeriv.suspensionDetails.suspended.toLowerCase() !== none) {
        return winDeriv.suspensionDetails.suspendedReason;
    }
    return undefined;
}

const isEarlyPrice = (winDeriv: WinDerivBase) => {
    return winDeriv.bettingState?.toLowerCase() === bettingPhases.early;
}

const isBoardPrice = (winDeriv: WinDerivBase) => {
    return winDeriv.bettingState?.toLowerCase() === bettingPhases.board;
}

const isStartingPrice = (winDeriv: WinDerivBase) => {
    return winDeriv.bettingState?.toLowerCase() === bettingPhases.starting;
}

const isIgnorePrice = (winDeriv: WinDerivBase) => {
    return winDeriv.bettingState?.toLowerCase() === bettingPhases.ignore;
}

const isBetmakerPrice = (winDeriv: WinDerivBase) => {
    return winDeriv.provider?.toLowerCase() === providers.betmakers;
}

const isDisplayOnly = (winDeriv: WinDerivBase) => {
    return !winDeriv.distributionDetails || !winDeriv.distributionDetails.isDistributed;
}

const isDistributed = (winDeriv: WinDerivBase) => {
    return winDeriv.distributionDetails && winDeriv.distributionDetails.isDistributed ? true : false;
}

export const getOverround = (selection: WinDerivSelection) => {
    // Individual price total is 100/((X+Y)/Y)=Z%....
    // x is the left part of the fraction and y is the last part - so if fract is 6/4 then x is 6 and y is 4.
    try {
        if (!selection.runnerIds || selection.runnerIds?.length === 0) { return 0; }
        if (!selection.priceFract || selection.priceFract === "0" || selection.priceFract === "-1") { return 0; }
        if (selection.priceFract === "1") { selection.priceFract = "1/1"; }
        const split = selection.priceFract.split("/");
        if (split.some((s) => !Number(s) && s !== "0")) { throw new Error("Contains values that aren't numbers"); }
        if (split.length !== 2) { throw new Error("Does not have a single divider"); }
        const x = Number(split[0]);
        const y = Number(split[1]);
        return 100 / ((x + y) / y);
    } catch (ex) {
        console.error(`Invalid price received: ${selection.priceFract}`, ex);
        return 0;
    }
}
